/** Conservative starter window — smaller than consumer LLM UIs (e.g. 200K). */
export const ADVISOR_MAX_CONTEXT_TOKENS = 16000;

/** Estimated system prompt + routing overhead sent with each request. */
const SYSTEM_PROMPT_TOKEN_OVERHEAD = 800;

const MESSAGE_FRAMING_TOKENS = 4;

const stripHtml = (html) => {
  if (typeof html !== "string") return "";
  try {
    const tmp = document.createElement("div");
    tmp.innerHTML = html;
    return (tmp.textContent || tmp.innerText || "").trim();
  } catch (_) {
    return html.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
  }
};

export const messageContentForTokenEstimate = (msg) => {
  if (!msg || typeof msg !== "object") return "";
  if (msg.role === "assistant" && msg.isHTML && typeof msg.content === "string") {
    return stripHtml(msg.content);
  }
  return msg.content == null ? "" : String(msg.content);
};

export const estimateTextTokens = (text) => {
  const s = text == null ? "" : String(text);
  if (!s.length) return 0;
  return Math.max(1, Math.ceil(s.length / 4));
};

export const formatTokenCount = (count) => {
  const n = Math.max(0, Math.round(count));
  if (n >= 1000) {
    const k = n / 1000;
    if (k >= 100) return `${Math.round(k)}K`;
    if (k >= 10) return `${Math.round(k)}K`;
    const rounded = Math.round(k * 10) / 10;
    return `${String(rounded).replace(/\.0$/, "")}K`;
  }
  return String(n);
};

export const estimateAdvisorContextUsage = ({
  messages = [],
  draftText = "",
  contextPayload = null,
  maxTokens = ADVISOR_MAX_CONTEXT_TOKENS,
} = {}) => {
  const limit = Math.max(1000, Number(maxTokens) || ADVISOR_MAX_CONTEXT_TOKENS);
  const hasConversationMessages = messages.some(
    (m) => m.role === "user" || m.role === "assistant"
  );
  const hasDraft = String(draftText || "").trim().length > 0;

  if (!hasConversationMessages && !hasDraft) {
    return {
      usedTokens: 0,
      maxTokens: limit,
      percent: 0,
      isNearLimit: false,
      isFull: false,
    };
  }

  let usedTokens = SYSTEM_PROMPT_TOKEN_OVERHEAD;

  if (contextPayload && typeof contextPayload === "object") {
    try {
      usedTokens += estimateTextTokens(JSON.stringify(contextPayload));
    } catch (_) {}
  }

  for (const msg of messages) {
    if (msg.role !== "user" && msg.role !== "assistant") continue;
    usedTokens += estimateTextTokens(messageContentForTokenEstimate(msg));
    usedTokens += MESSAGE_FRAMING_TOKENS;
  }

  usedTokens += estimateTextTokens(draftText);

  const ratio = limit > 0 ? usedTokens / limit : 0;
  const percent = Math.min(100, Math.max(0, Math.round(ratio * 100)));

  return {
    usedTokens,
    maxTokens: limit,
    percent,
    isNearLimit: percent >= 85,
    isFull: percent >= 100,
  };
};
